import { useMsal } from '@azure/msal-react';
import type { AssistantApiType } from '@labeling/assistant';
import { hc } from 'hono/client';
import { createContext, useContext, useMemo } from 'react';
import { useUserStore } from '../stores/user-store';
import { buildAuthenticatedFetch, buildSandboxFetch } from '../utils/authenticated-fetch';

export type AssistantClient = ReturnType<typeof hc<AssistantApiType>>;

type AssistantContextType = {
  client: AssistantClient;
};

const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

export const AssistantProvider = ({ children }: { children: React.ReactNode }) => {
  const msal = useMsal();
  const assumedRole = useUserStore((s) => s.assumedRole);
  const client = useMemo(() => {
    if (import.meta.env.MODE !== 'sandbox') {
      return hc<AssistantApiType>(import.meta.env.VITE_ASSISTANT_BASE_URL, {
        fetch: buildAuthenticatedFetch(msal, import.meta.env.VITE_CLIENT_ID),
      });
    }
    return hc<AssistantApiType>(import.meta.env.VITE_ASSISTANT_BASE_URL, {
      fetch: buildSandboxFetch(assumedRole),
    });
  }, [msal, assumedRole]);

  return <AssistantContext.Provider value={{ client }}>{children}</AssistantContext.Provider>;
};

export const useAssistantApi = (): { client: AssistantClient } => {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error('useAssistantApi must be used within an AssistantProvider');
  }

  return context;
};
